import merge from 'deepmerge';
import { compact, first, flatMap, groupBy, isEmpty, last, map, max, partition, sortBy, take, values } from 'lodash';
import { Locale } from 'locale-enum';

import { ItemStatusEnum } from '@pimm/services/lib/store-inspections';
import { FormInputSources } from '../consts';
import { isTempProbeManual } from '../_helper';
import { BaseReport, StoreFormReport } from './store-form-report';

namespace IDailyFoodSafetyReport {
  export type Item = Omit<BaseReport.Item, 'inputs'> & {
    dayparts: (Omit<BaseReport.Daypart, 'formGroups'> & { inputs: Record<string, BaseReport.Input[]> })[];
  };

  export type Section = Omit<BaseReport.Section, 'data'> & {
    data: Item[];
  };

  export type Form = Omit<BaseReport.Form, 'sections'> & {
    sections: Section[];
  };

  export type FormGroup = Omit<BaseReport.FormGroup, 'forms'> & {
    forms: Form[];
  };

  export type DaypartLeaders = Omit<BaseReport.Daypart, 'formGroups'>[];

  export type ReportDay = Omit<BaseReport.ReportDay, 'dayparts'> & {
    date: string;
    daypartLeaders: DaypartLeaders;
    formGroups: FormGroup[];
  };
}

export class DailyFoodSafetyReport extends StoreFormReport {
  public composeReport(locale: Locale): any {
    this.locale = locale;
    const formVariants = [undefined, 'singleDaypart', 'dualDaypart', 'multiDaypart'];
    const reportDays = this.reportData?.reportDays ?? [];

    /**
     * Any template version above or equal to 1.3+ is considered "new".
     * This mirrors the logic in `useDailyFoodSafetyReport` hook.
     */
    const isNewVersion = (version?: string) => {
      if (!version) return false;
      const [major = 0, minor = 0] = version.split('.').map(Number);
      return major > 1 || (major === 1 && minor >= 3);
    };

    const isNewTemplate = !this.reportTemplateVersion || isNewVersion(this.reportTemplateVersion);

    //#region Helpers

    /**
     * Merge two arrays of objects by a unique key.
     * If an object with the same key exists in both arrays,
     * it will be deeply merged using `deepmerge`.
     *
     * @param existing - The current array (accumulator).
     * @param incoming - The new array to merge in.
     * @param idKey - The property name that uniquely identifies an object.
     * @returns A new merged array of objects.
     */
    const mergeById = <T>(existing: T[], incoming: T[], idKey: keyof T): T[] => {
      const map = new Map<string, T>();

      // Store all existing entries in a Map for quick lookup by id
      existing.forEach((e: any) => map.set(e[idKey], e));

      incoming.forEach((i: any) => {
        const id = i[idKey];
        if (!id) return;

        if (map.has(id)) {
          // If already exists → deep merge incoming with existing
          map.set(id, merge(map.get(id)!, i, { arrayMerge }));
        } else {
          // If new → just insert
          map.set(id, i);
        }
      });

      return Array.from(map.values());
    };

    /**
     * Custom array merge strategy for deepmerge.
     * - If the array contains identifiable entities (by group/form/section/item id),
     *   merge them by ID instead of concatenating.
     * - If the array contains raw inputs, concatenate them.
     * - Otherwise, replace destination with source.
     */
    const arrayMerge = (dest: any[], source: any[], options: any): any[] => {
      if (dest.length && typeof dest[0] === 'object') {
        if ('packageDetailsGroupId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsGroupId');
        }
        if ('packageDetailsFormId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsFormId');
        }
        if ('packageDetailsSectionId' in dest[0]) {
          return mergeById(dest, source, 'packageDetailsSectionId');
        }
        if ('sectionLineItemId' in dest[0]) {
          return mergeById(dest, source, 'sectionLineItemId');
        }
        if ('inputs' in dest[0]) {
          // For input arrays, just concatenate (no merging by id)
          return dest.concat(source);
        }
      }
      // Default: replace with source
      return source;
    };

    /**
     * Flatten a form’s sections into a flat list of items
     * and attach daypart metadata to each item.
     *
     * @returns A IDailyFoodSafetyReport.Form with items and dayparts attached.
     */
    const mapItemsByForm = (_daypart: Partial<BaseReport.Daypart>, _form: BaseReport.Form) => {
      const sections = map(_form.sections, ({ data = [], ..._section }) => {
        const section: IDailyFoodSafetyReport.Section = {
          ..._section,
          data: data.map(({ inputs: _itemInputs, ..._item }) => {
            // Normalize inputs: pick the last recorded input for each key
            const inputs = Object.entries(_itemInputs).reduce((inputs: Record<string, BaseReport.Input[]>, [inputId, _inputs]) => {
              return { ...inputs, [inputId]: take(_inputs, 1) }; // get the last recorded input
            }, {});
            const item: IDailyFoodSafetyReport.Item = {
              dayparts: [{ ..._daypart, inputs }],
              ..._item,
            };
            return item;
          }),
        };
        return section;
      });

      const form: IDailyFoodSafetyReport.Form = {
        ..._form,
        sections: sections,
      };
      return form;
    };

    /**
     * Chunk a form by dayparts (same logic as opsbee daily-food-safety-report hook).
     * split into multiple forms with sliced dayparts.
     */
    const chunkFormByDayparts = (form: IDailyFoodSafetyReport.Form): IDailyFoodSafetyReport.Form[] => {
      const allItems = form.sections?.flatMap(s => s.data ?? []) ?? [];
      const daypartCount = Math.max(0, max(allItems.map(item => item.dayparts?.length ?? 0)) ?? 0);
      const daypartChunkSize = daypartCount >= 5 ? 3 : 2;
      if (daypartCount <= 3) return [form];
      const numChunks = Math.ceil(daypartCount / daypartChunkSize);
      return Array.from({ length: numChunks }, (_, chunkIndex) => {
        const startIndex = chunkIndex * daypartChunkSize;
        const endIndexExclusive = Math.min((chunkIndex + 1) * daypartChunkSize, daypartCount);
        const sampleItem = allItems.find(item => item.dayparts?.length);
        const daypartSlice = sampleItem?.dayparts?.slice(startIndex, endIndexExclusive) ?? [];
        const daypartStart = first(daypartSlice);
        const daypartEnd = last(daypartSlice) ?? first(daypartSlice);
        const title = `${this.translate(form.title, form.titleTranslations)}  ${daypartStart?.label === daypartEnd?.label ? '' : ` (${this.translate(daypartStart?.label, daypartStart?.translations)} - ${this.translate(daypartEnd?.label, daypartEnd?.translations)})`}`;
        return {
          ...form,
          title,
          titleTranslations: undefined,
          sections: map(form.sections, section => ({
            ...section,
            data: map(section.data, item => ({
              ...item,
              dayparts: (item.dayparts ?? []).slice(startIndex, endIndexExclusive),
            })),
          })),
        };
      });
    };

    //#endregion

    /**
     * STEP 1
     * Transform all reportDays by:
     *  - Flattening sections → items
     *  - Merging formGroups/forms/items across dayparts by ID
     *  - Attaching daypart metadata to each item
     *
     * @param reportDays - The raw BaseReport.ReportDay[]
     * @returns Flattened and merged IDailyFoodSafetyReport.ReportDay[]
     */
    const normalizedReportByDates = reportDays.reduce((_reportDays: IDailyFoodSafetyReport.ReportDay[], { dayparts, ..._reportDay }) => {
      const reportDay: IDailyFoodSafetyReport.ReportDay = {
        ..._reportDay,
        daypartLeaders: [],
        formGroups: [],
      };

      for (const { formGroups = [], ..._daypart } of dayparts) {
        if (!formGroups.length) continue;

        // Merge all dayparts for this reportDay
        const nextFormGroups = formGroups.map(({ forms = [], ..._formGroup }) => {
          return { forms: forms.map(_form => mapItemsByForm(_daypart, _form)), ..._formGroup };
        });

        // Merge with accumulated formGroups using custom arrayMerge
        reportDay.formGroups = merge(reportDay.formGroups, nextFormGroups, { arrayMerge });
        // Append daypart leader
        reportDay.daypartLeaders.push(_daypart);
      }

      return [..._reportDays, reportDay];
    }, []);

    /**
     * Step 2
     * Transform combined data into composer readable body
     */

    const reportDay = normalizedReportByDates[0];

    // TODO: create composer type
    const compiledData: Record<string, any> = {
      reports: [],
    };

    if (reportDay) {
      const reportBody: Record<string, any> = {
        ...this.composerBody(),
        formGroups: [],
        translations: {
          item: this.translate('item'),
          standard: this.translate('standard'),
          time: this.translate('time'),
          result: this.translate('result'),
          correctiveActions: this.translate('corrective_actions'),
          correctiveActionKeys: this.translate('corrective_action_keys'),
          completedBy: this.translate('completed_by'),
        },
      };

      // Page Leaders
      reportBody.reviews = {
        dayparts: reportDay.daypartLeaders.map(_ => {
          return this.composeReviewer({
            ..._.opsLeader,
            label: `${this.translate(_.label, _.translations)} - ${this.translate('ops_leader')}`,
          });
        }),
        generalManager: this.composeReviewer({
          ...reportDay.eodReview,
          useDefId: 'opsLeaderBorder',
          label: this.translate('general_manager'),
        }),
        districtManager: this.composeReviewer({
          ...reportDay.dmReview,
          label: this.translate('district_manager'),
        }),
      };

      // Page Body
      for (const _reportDay of normalizedReportByDates) {
        for (const { forms, ...formGroup } of _reportDay.formGroups) {
          if (!forms?.length) continue;

          const numOfForms = forms.length;
          const inputTypes = forms.flatMap(({ sections }) => {
            return sections.flatMap(({ data }) => compact(data.flatMap(_ => _.inputType?.toLowerCase())));
          });
          const inputSources = FormInputSources.filter(_ => inputTypes.some(_inputType => _inputType.includes(_.keyword)));

          // define form group header
          reportBody.formGroups.push({
            useDefId: 'formGroupHeader',
            title: this.translate(formGroup.title, formGroup.titleTranslations),
            inputSourceLabel: !isEmpty(inputSources) ? this.translate('temperature_input_sources') : undefined,
            inputSources: inputSources.map(_source => ({ ..._source, label: this.translate(_source.label) })),
          });

          const expandedForms = flatMap(forms, form => chunkFormByDayparts(form));
          for (const { sections, ...form } of expandedForms) {
            // Flatten all items from all sections in this form
            const allItems = sections.flatMap(_section => _section.data.filter(_ => _.status !== ItemStatusEnum.Excluded));

            if (!allItems.length) continue;

            // Partition items by tagId (like Opsbee)
            const [untaggedItems, itemsWithTags] = partition(allItems, _ => !_.tag?.id);

            // prettier-ignore
            const dayparts = allItems[0]?.dayparts.map(_ => ({ value: this.translate(_.label, _.translations) }))

            const numOfCols = dayparts.length;
            // find the variant using the number of dayparts. but if exceed, use the last variant or default to the first variant
            let variant = formVariants[numOfCols];

            if (!variant) {
              variant = numOfCols > formVariants.length - 1 ? formVariants.at(-1) : formVariants[1];
            }

            // define section box (only once per form)
            if (numOfForms > 1) {
              reportBody.formGroups.push({ useDefId: 'formHeader', title: this.translate(form.title, form.titleTranslations) });
            }

            const taggedItems = sortBy(values(groupBy(itemsWithTags, item => item.tag?.id ?? '_')), items => items[0]?.tag?.name);
            // Process sections: untagged items first, then tagged items
            for (const _items of [untaggedItems, ...taggedItems]) {
              if (!_items.length) continue;

              const tag = _items[0]?.tag;

              // Append Section header
              reportBody.formGroups.push({
                useDefId: `${variant}Header`,
                title: tag ? this.translate(tag.name, tag.translations) : this.translate('Item'),
                dayparts: dayparts,
              });

              // define item box for all items in this form
              for (const { dayparts, ..._item } of _items) {
                const item = this.composeItem(_item);

                const isSkipped = _item.status === ItemStatusEnum.Skipped;
                const isManualProbe = isTempProbeManual(_item.inputType);

                reportBody.formGroups.push({
                  ...item,
                  useDefId: `${variant}Item`,
                  // map all first input per daypart
                  dayparts: values(dayparts).map(_dp => {
                    const input = first(_dp.inputs?.[1]);
                    // prettier-ignore
                    const flattenInputs = values(_dp.inputs).map(_inputs => first(_inputs)).filter(_input => !!_input);
                    const _input = this.composeInput(flattenInputs, _item.inputType);
                    const inputIcon = FormInputSources.find(_ => input?.inputSource?.toLowerCase().includes(_.keyword))?.icon;
                    return { ..._input, inputSource: !isSkipped && isManualProbe && !!inputIcon ? inputIcon : undefined };
                  }),
                });
              }
            }
          }
        }
      }

      // Foot Notes
      const reportNotes = this.composeReportNotes(reportDays[0]);
      let formNotes: any[] = [];

      for (const _daypart of reportDays[0]?.dayparts) {
        const forms = compact(flatMap(_daypart.formGroups, _ => _.forms)).filter(_ => !!_.notes?.length);
        const notes = sortBy(forms, _ => this.translate(_.title, _.titleTranslations)).flatMap(_form =>
          this.composeFormNotes(_form, _daypart),
        );
        formNotes = formNotes.concat(notes);
      }

      if (isNewTemplate) {
        // New template behaviour (v1.3+): corrective actions & notes are rendered
        // as part of the formGroups stream, not via top-level fields.
        if (reportBody.correctiveActions?.length) {
          reportBody.formGroups.push({
            useDefId: 'correctiveActionBox',
            correctiveActions: reportBody.correctiveActions,
          });

          // Avoid exposing correctiveActions at the top level for new templates
          reportBody.correctiveActions = [];
        }

        // Append report notes into formGroups
        if (reportNotes.length > 0) {
          reportBody.formGroups.push({ useDefId: 'noteTitle', title: this.translate('report_notes') });
          reportNotes.forEach(_def => reportBody.formGroups.push(_def));
        }
        // Append form notes into formGroups
        if (formNotes.length > 0) {
          reportBody.formGroups.push({ useDefId: 'noteTitle', title: this.translate('form_notes') });
          formNotes.forEach(_def => reportBody.formGroups.push(_def));
        }
      } else {
        // Legacy template behaviour: use top-level `notes` collection
        if (reportNotes.length > 0) {
          reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('report_notes') });
          reportNotes.forEach(_def => reportBody.notes.push(_def));
        }
        if (formNotes.length > 0) {
          reportBody.notes.push({ useDefId: 'noteTitle', title: this.translate('form_notes') });
          formNotes.forEach(_def => reportBody.notes.push(_def));
        }

      }
      compiledData.reports.push(reportBody);
    }

    return compiledData.reports[0];
  }
}
